import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useContactSearch, type ContactSummary } from '../../../../lib/use-contact-search';
import { avatarSlotFor } from '../../../../lib/avatar-color';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import styles from './ContactPicker.module.css';
import { Avatar, IconButton, ScrollArea } from '../../ui';
import { RailCatcher } from '../../../RailCatcher';

const DEBOUNCE_MS = 220;

// The composer's "+ Contact" picker — a centered modal (mirrors the Symphony SearchDialog) with a
// debounced search box over the caller's CRM. Empty query shows a recent-contacts default list;
// picking a row adds the contact to the composer's pending selection (rendered as a chip) and closes.
export function ContactPicker({ onPick, onClose }: { onPick: (contact: ContactSummary) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi('contact-picker');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const search = useContactSearch(debounced);
  useLoadBi('contact-picker-data', {
    isLoading: search.status === 'loading',
    isError: search.status === 'error',
  });

  const pick = (contact: ContactSummary) => {
    logModalActionBi({ page_name: 'chat', modal_name: 'contact_picker', action: 'pick_contact' });
    onPick(contact);
    onClose();
  };

  return createPortal(
    <div
      className={styles.scrim}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <RailCatcher onClose={onClose} zIndex={60} />
      <div role="dialog" aria-modal="true" aria-label={t('chat.contactPicker.title')} className={styles.card}>
        <div className={styles.header}>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('chat.contactPicker.search')}
          />
          <IconButton size="tiny" aria-label={t('common.close')} onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </IconButton>
        </div>
        <div className={styles.divider} aria-hidden />
        <ScrollArea className={styles.body}>
          <Results state={search} onPick={pick} />
        </ScrollArea>
      </div>
    </div>,
    document.body,
  );
}

function Results({
  state,
  onPick,
}: {
  state: ReturnType<typeof useContactSearch>;
  onPick: (contact: ContactSummary) => void;
}) {
  const { t } = useTranslation();
  if (state.status === 'loading') {
    return (
      <>
        {[72, 60, 84].map((w, i) => (
          <div key={i} className={styles.shimmerRow}>
            <div className="orion-shimmer" style={{ height: 14, width: `${w}%`, borderRadius: 6 }} />
          </div>
        ))}
      </>
    );
  }
  if (state.status === 'error') return <div className={styles.empty}>{t('chat.contactPicker.error')}</div>;
  if (state.contacts.length === 0) return <div className={styles.empty}>{t('chat.contactPicker.empty')}</div>;
  return (
    <>
      {state.contacts.map((c) => (
        <ContactRow key={c.id} contact={c} onPick={onPick} />
      ))}
    </>
  );
}

function ContactRow({ contact, onPick }: { contact: ContactSummary; onPick: (contact: ContactSummary) => void }) {
  const subtitle = contact.phones[0] || contact.emails[0] || '';
  return (
    <button type="button" className={styles.row} onClick={() => onPick(contact)}>
      {/* The shared DS `Avatar`, coloured per contact rather than in the user's own brand colourway. */}
      <Avatar initials={initials(contact.name)} size={32} color={avatarSlotFor(contact.id)} />
      <span className={styles.rowText}>
        <span className={styles.rowName}>{contact.name}</span>
        {subtitle && <span className={styles.rowSubtitle}>{subtitle}</span>}
      </span>
    </button>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return `${first}${last}`.toUpperCase();
}
